import React, {useMemo, useState} from 'react';
import {Card, IconButton, Slider, TextField} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {SelectedGroupedItem} from './types/Inventory';
import {useSelector} from 'react-redux';
import {RootState} from '../../../store';
import {moneyFromMinor} from '../../utils/UnitConversion.utils';
import {RARITY_COLORS, RarityGrade} from '../../utils/RarityColors';
import {toast} from 'react-toastify';

type Props = {
  item: SelectedGroupedItem;
  onRemove: (key: string) => void;
  onQuantityChange: (key: string, quantity: number) => void;
  onPriceChange: (key: string, price: number) => void;
};

// debounce helper
const debounce = (fn: (...args: any[]) => void, delay = 100) => {
  let timer: any;
  return (...args: any[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

const SellItemCard: React.FC<Props> = ({
                                         item,
                                         onRemove,
                                         onQuantityChange,
                                         onPriceChange,
                                       }) => {
  const currencyRate = useSelector((state: RootState) => state.currency.rate);
  const currencySymbol = useSelector((state: RootState) => state.currency.symbol);

  // Commercial rounding helper to 2 decimal places
  const roundCommercial = (val: number) => Math.round((val + Number.EPSILON) * 100) / 100;

  // Enforce 0.02 absolute minimum for the initial item price
  const initialPriceRaw = item.price * currencyRate;
  const initialPrice = roundCommercial(initialPriceRaw < 0.02 ? 0.02 : initialPriceRaw);

  const [localQuantity, setLocalQuantity] = useState(item.quantity);
  const [localPrice, setLocalPrice] = useState(initialPrice);

  // local state for raw text input to allow fluid typing with commas/dots
  const [inputValue, setInputValue] = useState(String(initialPrice));

  // Sync state ONLY when currency rate changes or a fundamentally different item is targeted
  React.useEffect(() => {
    const rawSyncedPrice = item.price * currencyRate;
    const syncedPrice = roundCommercial(rawSyncedPrice < 0.02 ? 0.02 : rawSyncedPrice);
    // Prevent overriding if the calculated price matches our current local price to break the backend loop
    if (syncedPrice !== localPrice) {
      setLocalPrice(syncedPrice);
      setInputValue(String(syncedPrice));
    }
  }, [currencyRate, item.key]);

  // debounce handlers → prevents lag while sliders move
  const debouncedQuantityUpdate = useMemo(
      () =>
          debounce((qty: number) => {
            onQuantityChange(item.key, qty);
          }, 50),
      []
  );

  const debouncedPriceUpdate = useMemo(
      () =>
          debounce((priceInCurrency: number) => {
            const priceInBase = priceInCurrency / currencyRate;
            onPriceChange(item.key, priceInBase < 0.02 ? 0.02 : priceInBase);
          }, 50),
      []
  );

  // PRICE LOGIC
  const baseRaw =
      ((item.representative.catalog?.priceRealMinor != null &&
      moneyFromMinor(item.representative.catalog?.priceRealMinor) > 0
          ? moneyFromMinor(item.representative.catalog?.priceRealMinor)
          : item.price) ?? 0) * currencyRate;

  // Absolute min price defined by user is 0.02 directly in their current currency
  const absoluteMinConverted = 0.02;

  // Enforce base price to be at least 0.02 to avoid wrong percentage scale calculations
  const base = baseRaw <= absoluteMinConverted ? absoluteMinConverted : roundCommercial(baseRaw);

  // Dynamic slider range calculation based on the base price magnitude
  const isCheapItem = base < 1.00;

  const cheapMin = Math.max(absoluteMinConverted, roundCommercial(base * 0.8));
  const recMin = Math.max(absoluteMinConverted, roundCommercial(base * 0.95));
  const recMax = isCheapItem ? roundCommercial(base + 0.02) : roundCommercial(base * 1.05);

  // Provide at least 0.10 headroom for items under 1.00 USD/converted value
  const expMax = isCheapItem
      ? roundCommercial(base + 0.10)
      : roundCommercial(base * 1.2);

  const sliderMin = absoluteMinConverted;
  const sliderMax = Math.max(absoluteMinConverted, expMax);

  // Keep exact 0.01 cent steps for low value items to allow micro adjustments
  const priceStep = isCheapItem ? 0.01 : Math.max(0.01, roundCommercial(base * 0.01));

  const clamped = Math.min(Math.max(localPrice, sliderMin), sliderMax);
  const percent = Math.round((clamped / base) * 100);

  let priceLabel = '';
  let priceColor = '';

  if (clamped < recMin) {
    priceLabel = 'Cheap';
    priceColor = '#84cc16';
  } else if (clamped <= recMax) {
    priceLabel = 'Recommended';
    priceColor = '#3f51b5';
  } else {
    priceLabel = 'Expensive';
    priceColor = '#c2410c';
  }

  // Calculate dynamic gradient stops for the slider rail based on current limits
  const totalRange = sliderMax - sliderMin;
  const recMinPercent = totalRange > 0 ? ((recMin - sliderMin) / totalRange) * 100 : 0;
  const recMaxPercent = totalRange > 0 ? ((recMax - sliderMin) / totalRange) * 100 : 100;

  const img = item.representative.catalog?.imageLink ?? '';
  const rarityColor = RARITY_COLORS[item.representative.details?.rarity as RarityGrade] ?? '#ffffff';

  // WEAR SHORT
  const wear = item.representative.catalog?.wearName ?? '';
  const wearShort =
      wear === 'FACTORY_NEW'
          ? 'FN'
          : wear === 'MINIMAL_WEAR'
              ? 'MW'
              : wear === 'FIELD_TESTED'
                  ? 'FT'
                  : wear === 'WELL_WORN'
                      ? 'WW'
                      : wear === 'BATTLE_SCARRED'
                          ? 'BS'
                          : '';

  const isST = item.representative.details?.isStattrak;
  const isSN = item.representative.details?.isSouvenir;

  const handleBlur = () => {
    let finalPrice = Number(inputValue);

    if (isNaN(finalPrice) || finalPrice < absoluteMinConverted) {
      finalPrice = absoluteMinConverted;
    } else {
      finalPrice = roundCommercial(finalPrice);
    }

    setLocalPrice(finalPrice);
    setInputValue(String(finalPrice));

    const priceInBase = finalPrice / currencyRate;
    onPriceChange(item.key, priceInBase < 0.02 ? 0.02 : priceInBase);

    if (Number(inputValue) < absoluteMinConverted || isNaN(Number(inputValue))) {
      toast.info(`Der Mindestpreis beträgt ${currencySymbol} ${absoluteMinConverted.toFixed(2)}`);
    }
  };

  return (
      <Card className="bg-[#1c1f26] border border-gray-700">
        <div className="px-3 py-1">

          {/* HEADER */}
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              {/* rarity bar + image */}
              <div className="relative flex items-center">
                <div
                    style={{
                      width: '4px',
                      height: '48px',
                      borderRadius: '0px',
                      background: rarityColor,
                    }}
                />

                <div className="relative">
                  {img ? (
                      <img src={img} alt="" className="w-16 h-16 object-contain"/>
                  ) : (
                      <div className="w-16 h-16 bg-gray-700"/>
                  )}

                  <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '-4px',
                        transform: 'translateY(-50%)',
                        width: '60px',
                        height: '48px',
                        background: `linear-gradient(to right, ${rarityColor}33 0%, transparent 100%)`,
                        pointerEvents: 'none',
                      }}
                  />
                </div>
              </div>

              {/* NAME + WEAR */}
              <div className="flex flex-col w-40">
                  <div className="text-white font-semibold text-[0.80rem] leading-[1.1] line-clamp-2">
                  {item.representative.catalog?.groupName}
                </div>

                <div className="flex items-center gap-1">
                  {isST && (
                      <span className="text-orange-400 text-[0.70rem] font-bold">
                    ST
                  </span>
                  )}
                  {isSN && (
                      <span className="text-yellow-400 text-[0.70rem] font-bold">
                    SN
                  </span>
                  )}
                  {wearShort && (
                      <span className="text-[0.70rem] text-gray-300 font-semibold">
                    {wearShort}
                  </span>
                  )}
                </div>
              </div>
            </div>

            {/* PRICE INPUT */}
            <TextField
                value={inputValue}
                onChange={(e) => {
                  let val = e.target.value;
                  val = val.replace(',', '.');
                  if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
                    setInputValue(val);
                    if (val !== '' && !val.endsWith('.')) {
                      const numVal = roundCommercial(Number(val));
                      setLocalPrice(numVal);
                      debouncedPriceUpdate(numVal);
                    }
                  }
                }}
                onBlur={handleBlur}
                type="text"
                className="w-36"
                size="small"
                InputProps={{
                  startAdornment: <span className="text-gray-400 mr-1">{currencySymbol}</span>,
                }}
            />
          </div>

          {/* QUANTITY SLIDER */}
          {item.maxQuantity > 1 && (
              <div className="flex items-center gap-3 mt-2">
                <Slider
                    value={localQuantity}
                    min={1}
                    max={item.maxQuantity}
                    step={1}
                    onChange={(_, v) => {
                      const qty = Array.isArray(v) ? v[0] : (v as number);
                      setLocalQuantity(qty);
                      debouncedQuantityUpdate(qty);
                    }}
                    sx={{
                      flexGrow: 1,
                      height: 8,
                      '& .MuiSlider-thumb': {
                        width: 16,
                        height: 16,
                        backgroundColor: '#fff',
                        border: '2px solid #0d0e11',
                      },
                      '& .MuiSlider-track': {height: 8},
                      '& .MuiSlider-rail': {height: 8, opacity: 0.4},
                    }}
                />

                <span className="text-gray-300 min-w-[50px] text-right text-sm whitespace-nowrap">
              x {localQuantity}
            </span>
              </div>
          )}

          {/* PRICE SLIDER */}
          <div className="mt-2">
            <div className="flex items-center gap-3">
              <Slider
                  value={clamped}
                  min={sliderMin}
                  max={sliderMax}
                  step={priceStep}
                  onChange={(_, v) => {
                    const price = Array.isArray(v) ? v[0] : (v as number);
                    const roundedPrice = roundCommercial(price < 0.02 ? 0.02 : price);
                    setLocalPrice(roundedPrice);
                    setInputValue(String(roundedPrice));
                    debouncedPriceUpdate(roundedPrice);
                  }}
                  sx={{
                    flexGrow: 1,
                    height: 8,
                    '& .MuiSlider-thumb': {
                      width: 16,
                      height: 16,
                      backgroundColor: '#fff',
                      border: '2px solid #0d0e11',
                    },
                    '& .MuiSlider-track': {display: 'none'},
                    '& .MuiSlider-rail': {
                      height: 8,
                      borderRadius: 9999,
                      opacity: 1,
                      background:
                          `linear-gradient(to right, ` +
                          `#84cc16 0%, #84cc16 ${recMinPercent}%, ` +
                          `#3f51b5 ${recMinPercent}%, #3f51b5 ${recMaxPercent}%, ` +
                          `#c2410c ${recMaxPercent}%, #c2410c 100%)`,
                    },
                  }}
              />

              <span className="text-gray-300 min-w-[50px] text-right text-sm whitespace-nowrap">
              {percent}%
            </span>
            </div>

            <div className="flex justify-between items-center mt-1">
            <span
                className="text-sm font-semibold block"
                style={{color: priceColor}}
            >
              {priceLabel}
            </span>

              <div className="flex justify-end">
                <IconButton
                    onClick={() => onRemove(item.key)}
                    size="small"
                    sx={{color: '#dc2626'}}
                >
                  <DeleteIcon fontSize="small"/>
                </IconButton>
              </div>
            </div>
          </div>
        </div>
      </Card>
  );
};

export default React.memo(SellItemCard);